import { hasRole } from '@agent-platform/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  BookOpen,
  Bot,
  Braces,
  Building,
  FileText,
  Gauge,
  History,
  Globe,
  KeyRound,
  Cpu,
  LayoutDashboard,
  MessagesSquare,
  Puzzle,
  Settings,
  ScrollText,
  Target,
  Terminal,
  Users,
  Variable,
  Waypoints,
  Workflow,
  Wrench,
  type LucideIcon,
  Layers,
  DatabaseZap,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { useMe } from '@/lib/queries';
import { ProductLogo } from './product-logo';
import { TenantSwitcher } from './tenant-switcher';
import { UserMenu } from './user-menu';

interface NavItem {
  to:
    | '/'
    | '/llm-setup'
    | '/agents'
    | '/workflows'
    | '/prompts'
    | '/playground'
    | '/request-context'
    | '/sandboxes'
    | '/endpoints'
    | '/api-keys'
    | '/tools'
    | '/skills'
    | '/workspaces'
    | '/environment'
    | '/traces'
    | '/sessions'
    | '/conversations'
    | '/metrics'
    | '/scorers'
    | '/usage'
    | '/caching'
    | '/members'
    | '/audit-log'
    | '/settings'
    | '/platform/tenants';
  label: string;
  icon: LucideIcon;
}

/** Icon for each section, also shown in the page bar. Longest matching prefix wins. */
const ROUTE_ICONS: Array<[string, LucideIcon]> = [
  ['/sessions', Layers],
  ['/conversations', History],
  ['/platform/tenants', Building],
  ['/llm-setup', Cpu],
  ['/agents', Bot],
  ['/workflows', Workflow],
  ['/prompts', FileText],
  ['/playground', MessagesSquare],
  ['/request-context', Braces],
  ['/sandboxes', Terminal],
  ['/endpoints', Globe],
  ['/api-keys', KeyRound],
  ['/tools', Wrench],
  ['/mcp-servers', Wrench],
  ['/skills', Puzzle],
  ['/workspaces', BookOpen],
  ['/environment', Variable],
  ['/traces', Waypoints],
  ['/metrics', Gauge],
  ['/scorers', Target],
  ['/usage', BarChart3],
  ['/caching', DatabaseZap],
  ['/members', Users],
  ['/audit-log', ScrollText],
  ['/settings', Settings],
];

export function iconForPath(pathname: string): LucideIcon | undefined {
  if (pathname === '/') return LayoutDashboard;
  return ROUTE_ICONS.filter(([p]) => pathname === p || pathname.startsWith(`${p}/`)).sort(
    (a, b) => b[0].length - a[0].length,
  )[0]?.[1];
}

// Studio navigation: 30px rows, 13px medium text, a soft tint for the current page.
const ITEM =
  'h-[30px] rounded-[10px] px-3 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground [&>svg]:size-4 group-data-[collapsible=icon]:px-2';

function NavGroup({ label, items }: { label?: string; items: NavItem[] }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (items.length === 0) return null;
  return (
    <SidebarGroup className="px-2.5 py-1.5">
      {label && (
        <SidebarGroupLabel className="text-sidebar-foreground h-7 px-3 text-xs font-medium">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu className="gap-0.5">
        {items.map((item) => (
          <SidebarMenuItem key={item.to}>
            <SidebarMenuButton
              asChild
              className={ITEM}
              tooltip={item.label}
              isActive={item.to === '/' ? pathname === '/' : pathname.startsWith(item.to)}
            >
              <Link to={item.to}>
                <item.icon />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

export function AppSidebar() {
  const me = useMe();
  const role = me.activeTenant?.role;
  const admin = Boolean(role && hasRole(role, 'admin'));
  const member = Boolean(role);
  const only = (show: boolean, item: NavItem): NavItem[] => (show ? [item] : []);

  const build: NavItem[] = [
    ...only(member, { to: '/agents', label: 'Agents', icon: Bot }),
    ...only(member, { to: '/workflows', label: 'Workflows', icon: Workflow }),
    ...only(member, { to: '/prompts', label: 'Prompts', icon: FileText }),
    ...only(member, { to: '/tools', label: 'Tools & MCP', icon: Wrench }),
    ...only(member, { to: '/skills', label: 'Skills & Plugins', icon: Puzzle }),
    ...only(member, { to: '/workspaces', label: 'Knowledge', icon: BookOpen }),
    ...only(member, { to: '/sandboxes', label: 'Sandboxes', icon: Terminal }),
  ];
  const test: NavItem[] = [
    ...only(admin, { to: '/playground', label: 'Playground', icon: MessagesSquare }),
    ...only(member, { to: '/request-context', label: 'Request context', icon: Braces }),
    ...only(member, { to: '/scorers', label: 'Scorers', icon: Target }),
  ];
  const observe: NavItem[] = [
    ...only(member, { to: '/traces', label: 'Traces', icon: Waypoints }),
    ...only(admin, { to: '/sessions', label: 'Session context', icon: Layers }),
    ...only(admin, { to: '/conversations', label: 'Conversation history', icon: History }),
    ...only(member, { to: '/metrics', label: 'Metrics', icon: Gauge }),
    ...only(member, { to: '/usage', label: 'Usage', icon: BarChart3 }),
    ...only(member, { to: '/caching', label: 'Prompt caching', icon: DatabaseZap }),
  ];
  const deploy: NavItem[] = [
    ...only(member, { to: '/endpoints', label: 'Endpoints', icon: Globe }),
    ...only(member, { to: '/api-keys', label: 'API keys', icon: KeyRound }),
    ...only(member, { to: '/environment', label: 'Environment', icon: Variable }),
  ];
  const settings: NavItem[] = [
    ...only(member, { to: '/llm-setup', label: 'LLM setup', icon: Cpu }),
    ...only(member, { to: '/members', label: 'Members', icon: Users }),
    ...only(admin, { to: '/audit-log', label: 'Audit log', icon: ScrollText }),
  ];
  const platform: NavItem[] = me.user.isSuperAdmin
    ? [{ to: '/platform/tenants', label: 'Tenants', icon: Building }]
    : [];

  return (
    <Sidebar variant="inset" collapsible="icon" className="p-0">
      <SidebarHeader className="gap-2 px-2.5 pt-3 pb-1">
        <div className="flex h-8 items-center gap-2 px-2">
          <ProductLogo />
          <span className="text-foreground truncate text-[15px] font-medium tracking-tight group-data-[collapsible=icon]:hidden">
            Agent Platform
          </span>
        </div>
        <div className="group-data-[collapsible=icon]:hidden">
          <TenantSwitcher />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <nav aria-label="Main navigation">
          <NavGroup items={[{ to: '/', label: 'Overview', icon: LayoutDashboard }]} />
          <NavGroup label="Build" items={build} />
          <NavGroup label="Test" items={test} />
          <NavGroup label="Observe" items={observe} />
          <NavGroup label="Deploy" items={deploy} />
          <NavGroup label="Settings" items={settings} />
          <NavGroup label="Platform" items={platform} />
        </nav>
      </SidebarContent>
      <div className="px-0 pb-1">
        <NavGroup items={[{ to: '/settings', label: 'Settings', icon: Settings }]} />
      </div>
      <SidebarFooter className="flex-row items-center gap-1 border-t px-2.5 py-2 group-data-[collapsible=icon]:flex-col">
        <UserMenu />
        <SidebarTrigger
          aria-label="Collapse navigation"
          className="text-sidebar-foreground ml-auto hidden md:inline-flex group-data-[collapsible=icon]:ml-0"
        />
      </SidebarFooter>
    </Sidebar>
  );
}
